import {
  getHohishesHohishesGet,
  GetHohishesHohishesGetData,
  getHohishHohishesHohishUuidGet,
  GetHohishHohishesHohishUuidGetData,
  getJobsHohishesJobsGet,
  getPostingsHohishesHohishUuidPostingsGet,
  GetPostingsHohishesHohishUuidPostingsGetData,
  HohishType,
  JobType,
} from "@/codegen/openapi2";
import { getJobsHohishesJobsGetQueryKey } from "@/codegen/openapi2/@tanstack/react-query.gen";
import { type Options } from "@/codegen/openapi2/client/types.gen";

import { getTerritories } from "../geo/standalone-fetches";
import { TerritoriesType } from "../geo/types";
import { TWENTY_FOUR_STALE_WHILE_REVALIDATE, queryClient } from "../react-query";

import { EnrichedHohishPostingType, EnrichedHohishType } from "./types";

// Jobs
export const getJobsQueryFn = (): Promise<JobType[]> =>
  getJobsHohishesJobsGet({}).then(response => response.data as JobType[]);

export const getJobs = () =>
  queryClient.fetchQuery({
    queryKey: getJobsHohishesJobsGetQueryKey(),
    queryFn: getJobsQueryFn,
    ...TWENTY_FOUR_STALE_WHILE_REVALIDATE,
  });

interface JobGroup {
  label: string;
  jobs: JobType[];
}

const groupOrder: Record<any, number> = {
  Ops: 0,
  Sales: 1,
  Autres: 2,
};

export const getJobGroups = (): Promise<JobGroup[]> =>
  getJobs().then(jobs => {
    const jobGroups: Record<string, JobGroup> = {};
    jobs.forEach(job => {
      if (!job.assignable) {
        return;
      }

      if (!jobGroups[job.group]) {
        jobGroups[job.group] = {
          label: job.group,
          jobs: [],
        };
      }

      jobGroups[job.group].jobs.push(job);
    });

    return Object.values(jobGroups).sort((a, b) => groupOrder[a.label] - groupOrder[b.label]);
  });

const extractTerritoryLabels = (
  territoryUuids: string[],
  territories: TerritoriesType
): string[] => {
  const agencies = territories.agencies.filter(agency => territoryUuids.includes(agency.uuid));
  const zones = territories.zones.filter(zone => territoryUuids.includes(zone.uuid));
  const regions = territories.regions.filter(region => territoryUuids.includes(region.uuid));

  return [
    ...agencies.map(agency => agency.name),
    ...zones.map(zone => zone.name),
    ...regions.map(region => region.name),
  ];
};

const enrichHohish = (
  naive: HohishType,
  territories: TerritoriesType,
  jobs: JobType[]
): EnrichedHohishType => {
  const permanent = naive.current_postings.find(posting => posting.type === "permanent");
  const territoryUuids = permanent
    ? [...permanent.agency_uuids, ...permanent.zone_uuids, ...permanent.region_uuids]
    : [];
  return {
    ...naive,
    currentPermanentJob: permanent?.job ?? null,
    currentPermanentJobLabel: jobs.find(job => job.value === permanent?.job)?.label ?? null,
    currentPermanentStartsOn: permanent?.starts_on ?? null,
    currentPermanentEndsOn: permanent?.ends_on ?? null,
    territoryUuids,
    territoryLabels: extractTerritoryLabels(territoryUuids, territories),
  };
};

// Multiple hohishes
export const getEnrichedHohishesQueryFn = async (
  clientOptions: Options<GetHohishesHohishesGetData, true>
): Promise<EnrichedHohishType[]> => {
  const naive = await getHohishesHohishesGet({ ...clientOptions }).then(response => response.data);
  const [territories, jobs] = await Promise.all([getTerritories(), getJobs()]);
  return naive.map(n => enrichHohish(n, territories, jobs));
};

// Single hohish
export const getEnrichedHohishQueryFromNaive = async (
  naive: HohishType
): Promise<EnrichedHohishType> => {
  const [territories, jobs] = await Promise.all([getTerritories(), getJobs()]);
  return enrichHohish(naive, territories, jobs);
};

export const getEnrichedHohishQueryFn = async (
  clientOptions: Options<GetHohishHohishesHohishUuidGetData, true>
): Promise<EnrichedHohishType> => {
  const naive = await getHohishHohishesHohishUuidGet({ ...clientOptions }).then(
    response => response.data
  );
  return getEnrichedHohishQueryFromNaive(naive);
};

export const getEnrichedHohishPostingsQueryFn = async (
  clientOptions: Options<GetPostingsHohishesHohishUuidPostingsGetData, true>
): Promise<EnrichedHohishPostingType[]> => {
  const naive = await getPostingsHohishesHohishUuidPostingsGet({ ...clientOptions }).then(
    response => response.data
  );
  return naive.map(n => ({
    ...n,
    territoryUuids: ([] as string[]).concat(n.agency_uuids, n.zone_uuids, n.region_uuids),
  }));
};
